/**
 * Prospecting UI state (June 2026) — ephemeral selection for the Sales
 * working-area. Server state (campaigns / prospects / suppression) lives in
 * TanStack Query; this store holds which campaign the operator is viewing plus
 * the per-row selection set used by the bulk cold-send action bar.
 *
 * Deliberately tiny: no persist. The selection lives as a ``Set<string>`` whose
 * identity is replaced on every mutation, so a selector returning the whole Set
 * (``selectedProspectIds``) is referentially stable between renders and needs no
 * ``useShallow``. Scalar selectors (``selectedCampaignId``) are likewise safe.
 *
 * Discovery is async (Temporal) on the BE with no "done" signal — the FE polls
 * prospects every 10s. ``discoveringByCampaign`` records when a discovery was
 * kicked off (campaignId → started-at epoch ms) so the Leads view can show the
 * branded brain loader while the search is in flight, falling back after a
 * timeout so it can never spin forever.
 */
import { create } from "zustand";

/**
 * Safety cap on how long the "discovering" loader may show before we stop
 * waiting. The AUTHORITATIVE "done" signal is the ``prospecting_run_done`` WS
 * event (``useProspectingWsBridge``) — this cap only guards the rare case where
 * that frame is missed (reconnect). A discovery run does several deep searches
 * + an email-backfill pass, so it can legitimately run for minutes; the old
 * 120s cap made the loader give up mid-run and show "No leads yet" while work
 * was still going. Generous cap + per-round streaming (leads appear as each
 * round persists, clearing the loader via ``total > 0``) keep the UX honest.
 */
export const DISCOVERY_TIMEOUT_MS = 8 * 60_000;

interface ProspectingState {
  selectedCampaignId: string | null;
  /** Currently checked prospect ids for the bulk cold-send action bar. */
  selectedProspectIds: ReadonlySet<string>;
  /** campaignId → epoch ms when an async discovery was kicked off. */
  discoveringByCampaign: Record<string, number>;
  setSelectedCampaign: (id: string | null) => void;
  /** Toggle a single row's selection. */
  toggleProspect: (id: string) => void;
  /** Replace the selection wholesale (e.g. "select all visible"). */
  setSelectedProspects: (ids: ReadonlyArray<string>) => void;
  /** Clear the selection (after a send, or when switching campaign). */
  clearSelection: () => void;
  /** Flag a campaign as having an in-flight discovery (loader shows). */
  markDiscovering: (campaignId: string) => void;
  /** Drop the in-flight flag (leads arrived, or the timeout elapsed). */
  clearDiscovering: (campaignId: string) => void;
}

export const useProspectingStore = create<ProspectingState>((set) => ({
  selectedCampaignId: null,
  selectedProspectIds: new Set<string>(),
  discoveringByCampaign: {},
  setSelectedCampaign: (id) =>
    // Switching campaign abandons the old selection — ids are campaign-scoped.
    set({ selectedCampaignId: id, selectedProspectIds: new Set<string>() }),
  toggleProspect: (id) =>
    set((s) => {
      const next = new Set(s.selectedProspectIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedProspectIds: next };
    }),
  setSelectedProspects: (ids) =>
    set({ selectedProspectIds: new Set(ids) }),
  clearSelection: () => set({ selectedProspectIds: new Set<string>() }),
  markDiscovering: (campaignId) =>
    set((s) => ({
      discoveringByCampaign: { ...s.discoveringByCampaign, [campaignId]: Date.now() },
    })),
  clearDiscovering: (campaignId) =>
    set((s) => {
      if (!(campaignId in s.discoveringByCampaign)) return s;
      const next = { ...s.discoveringByCampaign };
      delete next[campaignId];
      return { discoveringByCampaign: next };
    }),
}));

/**
 * Whether ``campaignId`` has a discovery in flight — an entry exists and was
 * started within the timeout window. Pure helper so callers can derive it from
 * a scalar selector (the started-at epoch) without returning a fresh object.
 */
export function isDiscoveringFrom(startedAt: number | undefined): boolean {
  return startedAt !== undefined && Date.now() - startedAt < DISCOVERY_TIMEOUT_MS;
}
